import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { FetchAllUsersIfNeeded, UsersState } from '@wilson/account';
import { ExternalUsersSelectComponent } from '@wilson/clients/core';
import { ExternalUsersState } from '@wilson/clients/states';
import { LocalizationState } from '@wilson/localization';
import { downloadFile } from '@wilson/utils';
import { format } from 'date-fns';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { firstValueFrom } from 'rxjs';
import { ExportComplementaryCertificateV2ModalComponent } from '../components/qualification-v2-overview/modals/export-modal/export-complementary-certificate-v2-modal/export-zip-qualifications-v2-modal.component';
import { ExportQualificationV2ModalComponent } from '../components/qualification-v2-overview/modals/export-modal/export-qualification-v2-modal.component';
import { ExportZipQualificationsV2ModalComponent } from '../components/qualification-v2-overview/modals/export-modal/export-zip-qualifications-v2-modal/export-zip-qualifications-v2-modal.component';
import { SharedUserQualificationsV2Gateway } from '../gateways/shared-user-qualifications-v2.gateway';
import { UserQualificationsGateway } from '@wilson/qualification-v2/common';

@Injectable({
  providedIn: 'root',
})
export class QualificationExportService {
  private readonly nzModal = inject(NzModalService);
  private readonly translate = inject(TranslateService);
  private readonly userQualificationsGateway = inject(
    UserQualificationsGateway,
  );
  private readonly sharedUserQualificationsV2Gateway = inject(
    SharedUserQualificationsV2Gateway,
  );
  private readonly nzMessageService = inject(NzMessageService);
  private readonly store = inject(Store);

  constructor() {
    this.store.dispatch(new FetchAllUsersIfNeeded());
  }

  exportListModule(): void {
    const modalRef = this.nzModal.create({
      nzTitle: this.translate.instant(
        'page.qualifications.export_modal.csv.title',
      ),
      nzContent: ExportQualificationV2ModalComponent,
      nzOkText: this.translate.instant(
        'page.qualifications.export_modal.export',
      ),
      nzCancelText: this.translate.instant(
        'page.qualifications.export_modal.cancel',
      ),
      nzOkDisabled: false,
      nzOnOk: async (
        componentInstance: ExportQualificationV2ModalComponent,
      ) => {
        try {
          const lang = (this.translate.getCurrentLang() ?? '').split('-')[0];
          const response = await firstValueFrom(
            this.userQualificationsGateway.exportQualifications(
              componentInstance.selectedExportOption === 'all'
                ? []
                : componentInstance.userIds,
              lang,
            ),
          );

          const fileName = [
            'qualifications',
            format(new Date(), 'yyyyMMdd'),
          ].join('_');

          const blob = new Blob([response], { type: 'text/csv' });
          downloadFile(blob, fileName, 'csv');
          return true;
        } catch {
          this.nzMessageService.error(
            this.translate.instant('general.errorMessage'),
          );
          return true;
        }
      },
    });

    const instance = modalRef.getContentComponent();
    instance.validityChange.subscribe((isValid: boolean) => {
      modalRef.updateConfig({
        nzOkDisabled: !isValid,
      });
    });
  }

  exportQualificationForSingleUser(): void {
    const modalRef = this.nzModal.create({
      nzTitle: this.translate.instant(
        'page.qualifications.export_modal.zip_pdf.title',
      ),
      nzContent: ExportZipQualificationsV2ModalComponent,
      nzOkText: this.translate.instant(
        'page.qualifications.export_modal.export',
      ),
      nzCancelText: this.translate.instant(
        'page.qualifications.export_modal.cancel',
      ),
      nzOkDisabled: true,
      nzOnOk: async (
        componentInstance: ExportZipQualificationsV2ModalComponent,
      ) => {
        try {
          const lang = (this.translate.getCurrentLang() ?? '').split('-')[0];

          const user = await firstValueFrom(
            this.store.select(UsersState.userById(componentInstance.userId)),
          );

          const response = await firstValueFrom(
            this.userQualificationsGateway.exportZipQualifications(
              componentInstance.userId,
              lang,
            ),
          );

          let fileName = 'qualifications_with_certificates';
          if (user) {
            fileName = `${user.lastName}_${user.firstName}_qualifications_with_certificates`;
          }

          fileName = `${format(new Date(), 'yyyyMMdd')}_${fileName}`;

          const blob = new Blob([response], { type: 'application/zip' });
          downloadFile(blob, fileName, 'zip');
          return true;
        } catch {
          this.nzMessageService.error(
            this.translate.instant('general.errorMessage'),
          );
          return true;
        }
      },
    });

    const instance = modalRef.getContentComponent();
    instance.userIdChange.subscribe((userId: string) => {
      modalRef.updateConfig({
        nzOkDisabled: !userId,
      });
    });
  }

  exportQualificationsForSingleSharedUser(): void {
    const modalRef = this.nzModal.create({
      nzTitle: this.translate.instant(
        'page.qualifications.export_modal.zip_pdf.title',
      ),
      nzContent: ExternalUsersSelectComponent,
      nzOkText: this.translate.instant(
        'page.qualifications.export_modal.export',
      ),
      nzCancelText: this.translate.instant(
        'page.qualifications.export_modal.cancel',
      ),
      nzOkDisabled: true,
      nzOnOk: async (componentInstance: ExternalUsersSelectComponent) => {
        try {
          const externalUser = this.store.selectSnapshot(
            ExternalUsersState.userByUserId(componentInstance.selectedUserId),
          );

          const res = await firstValueFrom(
            this.sharedUserQualificationsV2Gateway.exportZipQualifications(
              componentInstance.selectedUserId,
              this.store.selectSnapshot(LocalizationState.getBackendLang),
            ),
          );

          return this.prepareAndDownloadQualificationsZip(
            res,
            externalUser?.user.firstName,
            externalUser?.user.lastName,
          );
        } catch (e) {
          console.error(e);
          return this.handleFailedDownload();
        }
      },
    });

    const instance = modalRef.getContentComponent();
    instance.selectedUserIdChange.subscribe((userId: string) => {
      modalRef.updateConfig({
        nzOkDisabled: !userId,
      });
    });
  }

  exportComplementaryCertificate(): void {
    const modalRef = this.nzModal.create({
      nzTitle: this.translate.instant(
        'page.qualifications.export_modal.complementary_certificate',
      ),
      nzContent: ExportComplementaryCertificateV2ModalComponent,
      nzOkText: this.translate.instant(
        'page.qualifications.export_modal.export',
      ),
      nzCancelText: this.translate.instant(
        'page.qualifications.export_modal.cancel',
      ),
      nzOkDisabled: true,
      nzOnOk: async (
        componentInstance: ExportComplementaryCertificateV2ModalComponent,
      ) => {
        try {
          const user = await firstValueFrom(
            this.store.select(UsersState.userById(componentInstance.userId)),
          );

          const response = await firstValueFrom(
            this.userQualificationsGateway.exportComplementaryCertificate(
              componentInstance.userId,
              this.store.selectSnapshot(LocalizationState.getBackendLang),
            ),
          );

          return this.prepareAndDownloadComplementaryCertificate(
            response,
            user?.firstName,
            user?.lastName,
          );
        } catch (e) {
          console.error(e);
          return this.handleFailedDownload();
        }
      },
    });

    const instance = modalRef.getContentComponent();
    instance.userIdChange.subscribe((userId: string) => {
      modalRef.updateConfig({
        nzOkDisabled: !userId,
      });
    });
  }

  public exportSharedUserQualificationsComplementaryCertificate(): void {
    const modalRef = this.nzModal.create({
      nzTitle: this.translate.instant(
        'page.qualifications.export_modal.complementary_certificate',
      ),
      nzContent: ExternalUsersSelectComponent,
      nzOkText: this.translate.instant(
        'page.qualifications.export_modal.export',
      ),
      nzCancelText: this.translate.instant(
        'page.qualifications.export_modal.cancel',
      ),
      nzOkDisabled: true,
      nzOnOk: async (componentInstance: ExternalUsersSelectComponent) => {
        try {
          const externalUser = this.store.selectSnapshot(
            ExternalUsersState.userByUserId(componentInstance.selectedUserId),
          );

          const res = await firstValueFrom(
            this.sharedUserQualificationsV2Gateway.exportComplementaryCertificate(
              componentInstance.selectedUserId,
              this.store.selectSnapshot(LocalizationState.getBackendLang),
            ),
          );

          return this.prepareAndDownloadComplementaryCertificate(
            res,
            externalUser?.user.firstName,
            externalUser?.user.lastName,
          );
        } catch (e) {
          console.error(e);
          return this.handleFailedDownload();
        }
      },
    });
    const instance = modalRef.getContentComponent();
    instance.selectedUserIdChange.subscribe((userId: string) => {
      modalRef.updateConfig({
        nzOkDisabled: !userId,
      });
    });
  }

  private prepareAndDownloadComplementaryCertificate(
    fileAsArrayBuffer: ArrayBuffer,
    firstName: string | undefined,
    lastName: string | undefined,
  ): boolean {
    let fileName = 'complementary_certificate';
    if (firstName && lastName) {
      fileName = `${lastName}_${firstName}_complementary_certificate`;
    }

    fileName = `${format(new Date(), 'yyyyMMdd')}_${fileName}`;

    const blob = new Blob([fileAsArrayBuffer], { type: 'application/pdf' });
    downloadFile(blob, fileName, 'pdf');
    return true;
  }

  private handleFailedDownload(): boolean {
    this.nzMessageService.error(this.translate.instant('general.errorMessage'));
    return true;
  }

  private prepareAndDownloadQualificationsZip(
    fileAsArrayBuffer: ArrayBuffer,
    firstName: string | undefined,
    lastName: string | undefined,
  ): boolean {
    let fileName = 'qualifications_with_certificates';
    if (firstName && lastName) {
      fileName = `${lastName}_${firstName}_qualifications_with_certificates`;
    }

    fileName = `${format(new Date(), 'yyyyMMdd')}_${fileName}`;

    const blob = new Blob([fileAsArrayBuffer], { type: 'application/zip' });

    downloadFile(blob, fileName, 'zip');
    return true;
  }
}
